import { useEffect, useMemo } from "react";
import useReactHookForm from "hooks/useReactHookForm";
import { useAppDispatch } from "hooks/redux";
import { Grid, Paper } from "@mui/material";
import DynamicForm from "shared/organisms/dynamicform";
import ContentRight from "shared/molecules/content-right";
import { TypographyHeading } from "shared/molecules/typography-layout";
import { useMutation } from "@apollo/client";
import { isEmpty } from "utils/helpers";
import { snackbarRequest } from "shared/molecules/snackbar/snackbar-slice";
import { useHistory, useLocation } from "react-router-dom";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Nl3PackageSchedule,
  Nl3PackageScheduleInitialValues,
  Nl3PackageScheduleMandatoryFields,
  Nl3PackageScheduleProperties,
  Nl3PackageScheduleValidationSchema,
  toNumberIdArray
} from "./helper";
import liveClassClientService from "utils/graphql/liveclassClient";
import { UPDATE_PACKAGE_SCHEDULE } from "utils/graphql/services/live-classes/mutations";
import { isFieldsValidate } from "shared/organisms/dynamicform/helper";
import { useGoogleWorkspaceMapping } from "./workspace-mapping";

export const EditNl3PackageSchedule = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation<{ row?: any }>();
  const row = location.state?.row;

  // liveSession now carries a projects list instead of a single projectId/projectName
  const sessionProjects = useMemo(
    () => (row?.liveSession?.projects || []).filter((p: any) => p?.id),
    [row]
  );
  const activePackages = (row?.liveclassPackages || []).filter(
    (pkg: any) => pkg?.isActive === true,
  );

  const editInitialValues: Nl3PackageSchedule = {
    ...Nl3PackageScheduleInitialValues,
    nlearn3LiveclassPackageIds: activePackages.map((pkg: any) => String(pkg.id)) as any,
    projectIds: sessionProjects.map((p: any) => String(p.id)) as any,
    vendorId: row?.liveSession?.vendorId ? String(row.liveSession.vendorId) : "",
    vendorAccountId: row?.liveSession?.vendorAccountId ? String(row.liveSession.vendorAccountId) : "",
    start_date_time: row?.liveSession?.scheduledStartTime ? (new Date(row.liveSession.scheduledStartTime) as any) : "",
    end_date_time: row?.liveSession?.scheduledEndTime ? (new Date(row.liveSession.scheduledEndTime) as any) : "",
    meetingTitle: row?.meetingTitle || "",
    subjectName: row?.subjectName || "",
    meetingSubTitle: row?.meetingSubTitle || "",
    thumbnailUrl: row?.thumbnailUrl || "",
    notesUrl: row?.notesUrl || "",
    departmentId: row?.departmentId ? String(row.departmentId) : "",
    designationId: row?.designationId ? String(row.designationId) : "",
    teacherId: row?.teacherId ? String(row.teacherId) : "",
    vendorUserId: row?.vendorUserId ? String(row.vendorUserId) : "",
  };

  const editFields = Nl3PackageScheduleProperties.map((f: any) => {
    if (f.id === "nlearn3LiveclassPackageIds" && row?.liveclassPackages?.length) {
      return {
        ...f,
        presetOptions: activePackages.map((pkg: any) => ({
          label: `${pkg?.name || ""} - ${pkg?.packageStudentsCount}`,
          value: String(pkg.id),
        })),
      };
    }

    if (f.id === "projectIds" && sessionProjects.length) {
      return {
        ...f,
        presetOptions: sessionProjects.map((p: any) => ({
          label: p.name,
          value: String(p.id),
        })),
      };
    }
    if (f.id === "departmentId" && row?.departmentId && row?.employeeDepartmentName) {
      return {
        ...f,
        presetOption: { label: row.employeeDepartmentName, value: String(row.departmentId) },
      };
    }
    if (f.id === "designationId" && row?.designationId && row?.employeeDesignationName) {
      return {
        ...f,
        presetOption: { label: row.employeeDesignationName, value: String(row.designationId) },
      };
    }
    if (f.id === "vendorId" && row?.liveSession?.vendorId && row?.liveSession?.vendorName) {
      return {
        ...f,
        disabled: true,
        presetOption: { label: row.liveSession.vendorName, value: String(row.liveSession.vendorId) },
      };
    }
    if (f.id === "vendorAccountId" && row?.liveSession?.vendorAccountId && row?.liveSession?.vendorAccountName) {
      return {
        ...f,
        disabled: true,
        presetOption: { label: row.liveSession.vendorAccountName, value: String(row.liveSession.vendorAccountId) },
      };
    }
    if (f.id === "teacherId" && row?.teacherId && row?.teacherName) {
      return {
        ...f,
        presetOption: {
          label: `${row.employeeId || ""} ${row.teacherName}`.trim(),
          value: String(row.teacherId),
        },
      };
    }
    if (f.id === "vendorUserId" && row?.vendorUserId) {
      return {
        ...f,
        presetOption: {
          label: row.vendorUserEmail,
          value: String(row.vendorUserId),
        },
      };
    }
    return f;
  });

  const { control, errors, getValues, watch, setValue } = useReactHookForm(
    editInitialValues,
    Nl3PackageScheduleValidationSchema,
    Nl3PackageScheduleMandatoryFields
  );

  const [UpdatePackageSchedule, { loading }] = useMutation(
    UPDATE_PACKAGE_SCHEDULE,
    {
      client: liveClassClientService,
    }
  );
  const {
    handleGoogleWorkspaceMappingError,
    dialog: workspaceMappingDialog,
  } = useGoogleWorkspaceMapping(loading);

  const handleUpdateNl3PackageSchedule = async () => {
    try {
      const response: any = await UpdatePackageSchedule({
        variables: {
          input: {
            id: Number(row?.id),
            projectIds: toNumberIdArray(watch()?.projectIds),
            employeeDepartmentId: Number(watch()?.departmentId),
            employeeDesignationId: Number(watch()?.designationId),
            scheduledStartTime: watch()?.start_date_time,
            scheduledEndTime: watch()?.end_date_time,
            liveclassPackageIds: toNumberIdArray(watch()?.nlearn3LiveclassPackageIds),
            meetingTitle: watch()?.meetingTitle,
            subjectName: watch()?.subjectName,
            meetingSubTitle: watch()?.meetingSubTitle,
            teacherId: Number(watch()?.teacherId),
            vendorUserId: Number(watch()?.vendorUserId),
            isAllowed: false,
          },
        },
      });

      const message = response?.data?.updatePackageSchedule?.message;

      if (!isEmpty(message)) {
        dispatch(
          snackbarRequest({
            message,
          })
        );
        history.replace(`/live-classes/package-schedules`);
      } else {
        dispatch(
          snackbarRequest({
            message: `Failed to update Package Schedule`,
            type: "error",
          })
        );
      }
    } catch (err: any) {
      const graphQLError = err?.graphQLErrors?.[0];
      if (handleGoogleWorkspaceMappingError(err)) {
        return;
      }

      dispatch(
        snackbarRequest({
          message:
            graphQLError?.message ||
            err?.message ||
            "Failed to update Package Schedule",
          type: "error",
        })
      );
    }
  };

  watch();

  useEffect(() => {
    if (!row) {
      history.replace(`/live-classes/package-schedules`);
    }
  }, [row]);

  if (!row) {
    return null;
  }

  return (
    <div>
      <TypographyHeading>Edit Package Schedule</TypographyHeading>
      <Paper elevation={2} sx={{ px: 2, pb: 2, pt: 2 }}>
        <Grid container spacing={2}>
          <DynamicForm
            fields={editFields}
            control={control}
            errors={errors}
            getValues={getValues}
            setValue={setValue}
            watch={watch}
          />
          <ContentRight sx={{ mt: 2 }}>
            <LoadingButton
              variant="contained"
              loadingPosition="start"
              sx={{
                width: "200px",
                height: "40px",
                marginTop: "15px",
                marginRight: "10px",
                padding: "7px 23.5px 7px 23.5px",
                cursor: "pointer",
                textAlign: "center",
              }}
              onClick={handleUpdateNl3PackageSchedule}
              loading={loading}
              disabled={!isFieldsValidate(errors, Nl3PackageScheduleMandatoryFields, getValues)}
            >
              Update
            </LoadingButton>
          </ContentRight>
        </Grid>
      </Paper>
      {workspaceMappingDialog}
    </div>
  );
};
